import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { OccasionCalendar } from '../components/occasions/OccasionCalendar';
import { OccasionModal } from '../components/occasions/OccasionModal';
import { CustomizationWizard } from '../components/occasions/CustomizationWizard';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Sparkles, Gift, Heart, Wand2 } from 'lucide-react';
export function OccasionsPage() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCustomizationWizard, setShowCustomizationWizard] = useState(false);
  const {
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };
  const handleChooseDate = () => {
    if (selectedDate) {
      setShowModal(true);
    }
  };
  const handleOccasionComplete = (occasion: string) => {
    setShowModal(false);
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store occasion details in sessionStorage to retrieve after login
      sessionStorage.setItem('pendingOccasion', JSON.stringify({
        date: selectedDate?.toISOString(),
        occasion: occasion
      }));
      // Redirect to login with return URL
      navigate('/login?redirect=/occasions&action=customize');
    } else {
      // User is logged in, proceed to customization
      // TODO: Navigate to customization page with occasion details
      alert(`Great! Let's customize your ${occasion} celebration for ${selectedDate?.toLocaleDateString()}`);
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-2757">
      <Navbar data-id="element-2758" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-gradient-to-b from-rose-50/50 to-white" data-id="element-2759">
        {/* Decorative Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-2760">
          <motion.div animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 left-[10%] w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-2761" />
          <motion.div animate={{
          y: [0, 30, 0],
          rotate: [0, -5, 0]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute top-40 right-[10%] w-72 h-72 bg-rose-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-2762" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2763">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2764">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-rose-100 shadow-sm mb-6" data-id="element-2765">
              <Sparkles className="w-4 h-4 text-rose-400 mr-2" data-id="element-2766" />
              <span className="text-sm font-medium text-gray-600" data-id="element-2767">
                Plan Your Celebration
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6" data-id="element-2768">
              Every Occasion Deserves{' '}
              <span className="text-rose-500" data-id="element-2769">Something Special</span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-2770">
              Select your celebration date and let us help you create the
              perfect gift to make it unforgettable.
            </p>

            {/* Customize Button */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-2771">
              <Button size="lg" onClick={() => setShowCustomizationWizard(true)} leftIcon={<Wand2 className="w-5 h-5" data-id="element-2773" />} className="shadow-xl hover:shadow-2xl transition-all" data-id="element-2772">
                Customize Your Gift
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-16 bg-white" data-id="element-2774">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2775">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2776">
            {/* Left Side - Info */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2777">
              <h2 className="font-serif text-4xl font-bold text-gray-900 mb-6" data-id="element-2778">
                Choose Your Date
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-2779">
                Pick the special date you're celebrating. Whether it's coming up
                soon or months away, we'll help you plan the perfect surprise.
              </p>

              <div className="space-y-4 mb-8" data-id="element-2780">
                {[{
                icon: Calendar,
                text: 'Select any future date from the calendar'
              }, {
                icon: Gift,
                text: "Tell us what you're celebrating"
              }, {
                icon: Heart,
                text: "We'll create something memorable"
              }].map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-start gap-3" data-id="element-2781">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-2782">
                      <item.icon className="w-5 h-5 text-rose-500" data-id="element-2783" />
                    </div>
                    <p className="text-gray-700 pt-2" data-id="element-2784">{item.text}</p>
                  </motion.div>)}
              </div>

              {selectedDate && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} data-id="element-2785">
                  <Button size="lg" onClick={handleChooseDate} className="w-full sm:w-auto" data-id="element-2786">
                    Continue with Selected Date
                  </Button>
                </motion.div>}
            </motion.div>

            {/* Right Side - Calendar */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex justify-center" data-id="element-2787">
              <OccasionCalendar selectedDate={selectedDate} onDateSelect={handleDateSelect} data-id="element-2788" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Occasions Section */}
      <section className="py-16 bg-rose-50/30" data-id="element-2789">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2790">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-12" data-id="element-2791">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4" data-id="element-2792">
              Popular Occasions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2793">
              From birthdays to weddings, we've got you covered for every
              celebration
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-2794">
            {[{
            emoji: '🎂',
            label: 'Birthdays',
            color: 'from-rose-400 to-pink-500'
          }, {
            emoji: '💕',
            label: 'Anniversaries',
            color: 'from-purple-400 to-pink-500'
          }, {
            emoji: '🎓',
            label: 'Graduations',
            color: 'from-blue-400 to-indigo-500'
          }, {
            emoji: '💍',
            label: 'Weddings',
            color: 'from-rose-400 to-rose-600'
          }, {
            emoji: '👶',
            label: 'Baby Showers',
            color: 'from-cyan-400 to-blue-500'
          }, {
            emoji: '💐',
            label: 'Engagements',
            color: 'from-pink-400 to-rose-500'
          }, {
            emoji: '❤️',
            label: "Valentine's",
            color: 'from-red-400 to-rose-500'
          }, {
            emoji: '🌸',
            label: "Mother's Day",
            color: 'from-pink-400 to-purple-500'
          }].map((occasion, index) => <motion.div key={occasion.label} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} whileHover={{
            y: -4
          }} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-all" data-id="element-2795">
                <div className="text-4xl mb-3" data-id="element-2796">{occasion.emoji}</div>
                <h3 className="font-medium text-gray-900" data-id="element-2797">{occasion.label}</h3>
              </motion.div>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-2798" />

      {/* Occasion Selection Modal */}
      {selectedDate && <OccasionModal isOpen={showModal} onClose={() => setShowModal(false)} selectedDate={selectedDate} onComplete={handleOccasionComplete} data-id="element-2799" />}

      {/* Customization Wizard */}
      <CustomizationWizard isOpen={showCustomizationWizard} onClose={() => setShowCustomizationWizard(false)} data-id="element-2800" />
    </div>;
}